import React, { useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Card, Col, Descriptions, Row, Space, Tag, Typography } from "antd";
// ANT ICONS
import { CheckCircleOutlined, CloseCircleOutlined, ClusterOutlined, ApiOutlined } from "@ant-design/icons";
// FONT AWESOME LIBRARY AND ICONS
import { faBook } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
// INTERFACES
import { BankInterface } from "../../../interfaces/Bank.interface";
import { ChannelInterface } from "../../../interfaces/Channel.interface";
import { ServerInterface } from "../../../interfaces/Server.interface";
import { AdapterInterface } from "../../../interfaces/Adapter.interface";
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
// CONSTANTS
import { BLANK_CHANNEL } from "../../../constants/channels";
import { BLANK_SERVER } from "../../../constants/server";
import { BLANK_ADAPTER } from "../../../constants/adapter";
import { BLANK_ORGANIZATION } from "../../../constants/organization";

export default function BankDetailsCard({
	bank,
	isLoadingGetOneBank,
}: {
	bank: BankInterface;
	isLoadingGetOneBank: boolean;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const channel: ChannelInterface = bank?.channel ? bank.channel : BLANK_CHANNEL;
	const server: ServerInterface = bank?.channel?.server ? bank.channel.server : BLANK_SERVER;
	const adapter: AdapterInterface = bank?.adapter ? bank.adapter : BLANK_ADAPTER;
	const org: OrganizationInterface = bank?.channel?.server?.org ? bank.channel.server.org : BLANK_ORGANIZATION;
	const { Text } = Typography;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const yesNoTag = (value: boolean, yesLabel = "Yes", noLabel = "No") =>
		value ? (
			<Tag icon={<CheckCircleOutlined />} color='success'>
				{yesLabel}
			</Tag>
		) : (
			<Tag icon={<CloseCircleOutlined />} color='default'>
				{noLabel}
			</Tag>
		);

	const safeText = (value: any, fallback = "—") => {
		if (value === null || value === undefined || value === "") return fallback;
		return String(value);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {}, []);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Card
			loading={isLoadingGetOneBank}
			title={
				<Space size={12}>
					<div>
						<Space size={8} wrap>
							<Tag color='cyan'>{safeText(bank.name)}</Tag>
							{bank.active ? (
								<Tag color='green'>{safeText("Active")}</Tag>
							) : (
								<Tag color='red'>{safeText("Inactive")}</Tag>
							)}
							{bank.type === "ELEVATOR" && <Tag color='blue'>{safeText(bank.type)}</Tag>}
							{bank.type === "ESCALATOR" && <Tag color='volcano'>{safeText(bank.type)}</Tag>}
							{bank.type === "MOVINGWALK" && <Tag color='green'>{safeText(bank.type)}</Tag>}
							{bank.type === "ELEVATOR" && <> {yesNoTag(bank.rear_doors, "Rear doors", "No rear doors")}</>}
							{bank.cmd_binary_control ? <Tag color='geekblue'>Binary Control</Tag> : <Tag>JSON Control</Tag>}
						</Space>
					</div>
				</Space>
			}
			bordered
		>
			<Row gutter={[16, 16]}>
				<Col xs={24}>
					<div className='overviewGrid'>
						<div className='generalStyles__mrFix mb-3'>
							<DrawerRowDivider icon={faBook} title='Overview' />
							<Descriptions bordered size='small' column={1} labelStyle={{ width: 170 }}>
								<Descriptions.Item label='Bank ID' className='generalStyles__textEllipsis'>
									<div className='generalStyles__textEllipsis'>{safeText(bank.id)}</div>
								</Descriptions.Item>
								<Descriptions.Item label='Channel ID'>
									<div className='generalStyles__textEllipsis'>{safeText(bank.channel_id)}</div>
								</Descriptions.Item>
								<Descriptions.Item label='Logical Port'>{safeText(bank.logical_port)}</Descriptions.Item>
								<Descriptions.Item label='Board'>{safeText(bank.board)}</Descriptions.Item>
								<Descriptions.Item label='Stops'>{safeText(bank.stops)}</Descriptions.Item>
								<Descriptions.Item label='Columns'>{safeText(bank.columns)}</Descriptions.Item>
								<Descriptions.Item label='Floor Count'>{safeText(bank.floor_count)}</Descriptions.Item>
								<Descriptions.Item label='Start Floor No.'>{safeText(bank.start_floor_no)}</Descriptions.Item>
								<Descriptions.Item label='Below Lobby Floors'>{safeText(bank.floor_below_lobby)}</Descriptions.Item>
							</Descriptions>
						</div>
						<div className='generalStyles__mrFix mb-3 '>
							<DrawerRowDivider icon={faBook} title='Channel' />
							<Descriptions bordered size='small' column={1} labelStyle={{ width: 170 }}>
								<Descriptions.Item label='ID'>
									<Space>
										<ClusterOutlined />
										{safeText(channel?.id)}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label='Name'>
									<Space>
										<ClusterOutlined />
										{safeText(channel?.name)}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label='Message Type'>
									{channel?.message_type ? <Tag color='purple'>{channel.message_type}</Tag> : "—"}
								</Descriptions.Item>
								<Descriptions.Item label='Connection'>
									{channel?.connection_type ? <Tag color='cyan'>{channel.connection_type}</Tag> : "—"}
								</Descriptions.Item>
								<Descriptions.Item label='Socket Address'>{safeText(channel?.socket_address)}</Descriptions.Item>
								<Descriptions.Item label='Reporting Socket'>
									{safeText(channel?.reporting_socket_address)}
								</Descriptions.Item>
								<Descriptions.Item label='Vendor Socket'>{safeText(channel?.vendor_socket_address)}</Descriptions.Item>
								<Descriptions.Item label='Heartbeat (ms)'>{safeText(channel?.heartbeat_frequency)}</Descriptions.Item>
								<Descriptions.Item label='Baud Rate'>{safeText(channel?.baud_rate)}</Descriptions.Item>
								<Descriptions.Item label='Active'>{yesNoTag(!!channel?.active, "Yes", "No")}</Descriptions.Item>
							</Descriptions>
						</div>
						<div className='generalStyles__mrFix mb-3'>
							<DrawerRowDivider icon={faBook} title='Server' />
							<Descriptions bordered size='small' column={1} labelStyle={{ width: 170 }}>
								<Descriptions.Item label='ID'>
									<Space>
										<ApiOutlined />
										{safeText(server?.id)}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label='Name'>
									<Space>
										<ApiOutlined />
										{safeText(server?.name)}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label='IP'>{safeText(server?.ip)}</Descriptions.Item>
								<Descriptions.Item label='Sync Schedule'>{safeText(server?.sync_sched)}</Descriptions.Item>
								<Descriptions.Item label='Quest DB Sync'>{safeText(server?.sync_quest_db_sched)}</Descriptions.Item>
								<Descriptions.Item label='Record Sync Size'>{safeText(server?.record_sync_size)}</Descriptions.Item>
								<Descriptions.Item label='FB Enabled'>{yesNoTag(!!server?.fb_enabled, "Yes", "No")}</Descriptions.Item>
								<Descriptions.Item label='Active'>{yesNoTag(!!server?.active, "Yes", "No")}</Descriptions.Item>

								<Descriptions.Item label='Organization'>
									{org ? (
										<Space direction='vertical' size={0}>
											<Text strong>{safeText(org.name)}</Text>
											<Text type='secondary'>
												{safeText(org.city)} • {safeText(org.state)} • {safeText(org.country)}
											</Text>
											<Text type='secondary'>{safeText(org.address)}</Text>
										</Space>
									) : (
										"—"
									)}
								</Descriptions.Item>
							</Descriptions>
						</div>
						<div className='generalStyles__mrFix mb-3'>
							<DrawerRowDivider icon={faBook} title='Adapter' />
							<Descriptions bordered size='small' column={1} labelStyle={{ width: 170 }}>
								<Descriptions.Item label='ID'>
									<Space>
										<ApiOutlined />
										{safeText(adapter?.id)}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label='Name'>
									<Space>
										<ApiOutlined />
										{safeText(adapter?.name)}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label='Socket address'>{safeText(adapter?.socket_address)}</Descriptions.Item>
								<Descriptions.Item label='Auth type'>{safeText(adapter?.auth_type)}</Descriptions.Item>
								<Descriptions.Item label='Type'>{safeText(adapter?.type)}</Descriptions.Item>
								<Descriptions.Item label='Protocol type'>{safeText(adapter?.protocol_type)}</Descriptions.Item>
								<Descriptions.Item label='Protocol ID'>{safeText(adapter?.protocol_id)}</Descriptions.Item>
								<Descriptions.Item label='Leading zeroes ID'>{safeText(adapter?.leading_zeroes_id)}</Descriptions.Item>
								<Descriptions.Item label='Lift type'>{safeText(adapter?.lift_type)}</Descriptions.Item>
								<Descriptions.Item label='Total bacnet channels'>
									{safeText(adapter?.total_bacnet_channels)}
								</Descriptions.Item>
							</Descriptions>
						</div>
						{bank?.lifts && (
							<div className='generalStyles__mrFix mb-3'>
								<DrawerRowDivider icon={faBook} title='Lifts' />
								<Descriptions bordered size='small' column={1} labelStyle={{ width: 170 }}>
									{bank.lifts.map((lift) => (
										<>
											<Descriptions.Item className='generalStyles__textEllipsis' label={lift?.name || "--"}>
												<Space>
													<ApiOutlined />
													{safeText(lift?.id || "--")}
												</Space>
											</Descriptions.Item>
										</>
									))}
								</Descriptions>
							</div>
						)}
					</div>
				</Col>
			</Row>
		</Card>
	);
}
